import { useEffect, useMemo, useState } from 'react';
import { NudosButton, NudosMaskedNumberInput } from 'components/NudosComponents';
import { Controller, ControllerRenderProps, useFormContext, useWatch } from 'react-hook-form';
import { ILogisticServiceEditionForm } from '../../LogisticServiceEdition.types';
import useSWR from 'swr';
import { getConfigClassesForLogisticPriceEdition } from 'services/product-configuration.service';
import { getLogisticServiceTypes } from 'services/priceEdition/logisticServicesPriceEdition.service';
import BordModalConfirm from 'components/NudosComponents/BordModalConfirm/BordModalConfirm';
import { SWR_KEYS } from 'constants/constants';
import { CURRENCIES } from 'types/bussines';
import { LOCAL_PRICE_INPUT_MODE } from 'types/global';
import BordSelectDropdown from 'components/NudosComponents/BordSelectDropdown/BordSelectDropdown';
import { CONFIG_CLASS_IDS_VISIBLE_IN_PRICE_EDITION, isProductPriceEditable } from 'utils/logisticServiceEdition';
import './EditableServiceRow.scss';

enum DROPDOWN_OPTION_SOURCE {
  LOGISTIC_SERVICE_TYPE = 'logisticServiceType',
  CONFIG_CLASS = 'configClass'
}

interface IUnifiedDropdownOption {
  id: number;
  name: string;
  source: DROPDOWN_OPTION_SOURCE;
}

interface IEditableServiceRowProps {
  removeAction: () => void;
  registerReference: `products.${number}.logisticServiceProductPrices.${number}`;
  productName: string;
  productSerial: string;
}

const EditableServiceRow = ({
  removeAction,
  registerReference,
  productName,
  productSerial
}: IEditableServiceRowProps) => {
  const { control, getValues, setValue } = useFormContext<ILogisticServiceEditionForm>();
  const exchangeRate = useWatch({ control, name: 'exchangeRate' });
  const service = useWatch({ control, name: registerReference });
  const { data: LSTypes } = useSWR(SWR_KEYS.listLSTypes, () => getLogisticServiceTypes());
  const { data: configClasses } = useSWR([SWR_KEYS.listConfigClasses], () => getConfigClassesForLogisticPriceEdition());

  const unifiedDropdownOptions = useMemo((): IUnifiedDropdownOption[] => {
    const lsOptions: IUnifiedDropdownOption[] = (LSTypes || []).map(type => ({
      id: type.id,
      name: type.name,
      source: DROPDOWN_OPTION_SOURCE.LOGISTIC_SERVICE_TYPE
    }));

    const configOptions: IUnifiedDropdownOption[] = (configClasses || [])
      .filter(cls => CONFIG_CLASS_IDS_VISIBLE_IN_PRICE_EDITION.includes(cls.id))
      .map(cls => ({
        id: cls.id,
        name: cls.name,
        source: DROPDOWN_OPTION_SOURCE.CONFIG_CLASS
      }));

    return [...lsOptions, ...configOptions];
  }, [LSTypes, configClasses]);

  const [localPriceInputMode, setLocalPriceInputMode] = useState<LOCAL_PRICE_INPUT_MODE>(LOCAL_PRICE_INPUT_MODE.HIDDEN);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const showLocalPriceInput =
    localPriceInputMode === LOCAL_PRICE_INPUT_MODE.EDITABLE || localPriceInputMode === LOCAL_PRICE_INPUT_MODE.READONLY;

  const getLocalPriceInputMode = () => {
    const currency = getValues('currency');
    switch (currency) {
      case CURRENCIES.USD: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.HIDDEN);
        break;
      }
      case CURRENCIES.ARS: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.EDITABLE);
        break;
      }
      case CURRENCIES.BRL: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.EDITABLE);
        break;
      }
      default: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.READONLY);
        break;
      }
    }
  };

  const recalculateExchangeRate = () => {
    const products = getValues('products') || [];
    const totalValueUsd = products.reduce((acc, product) => {
      return (
        acc +
        (Number(
          product?.logisticServiceProductPrices?.reduce((acc, service) => acc + (Number(service?.totalValueUsd) || 0), 0) || 0
        ) || 0)
      );
    }, 0);
    const totalValueLocal = products.reduce((acc, product) => {
      return (
        acc +
        (Number(
          product?.logisticServiceProductPrices?.reduce(
            (acc, service) => acc + (Number(service?.totalValueLocal) || 0),
            0
          )
        ) || 0)
      );
    }, 0);
    const newExchangeRate = (totalValueLocal / totalValueUsd);
    setValue('exchangeRate', newExchangeRate);
  };

  const handleBlurLocalPrice = (
    text: string = '',
    field: ControllerRenderProps<
      ILogisticServiceEditionForm,
      `products.${number}.logisticServiceProductPrices.${number}`
    >
  ) => {
    let newLocalPrice = Number(text) || 0;
    if (localPriceInputMode !== LOCAL_PRICE_INPUT_MODE.EDITABLE)
      return field.onChange({ ...field.value, totalValueLocal: newLocalPrice });
    const newExchangeRate = Math.ceil((newLocalPrice / Number(field.value.totalValueUsd)) * 100) / 100;
    newLocalPrice = Number(field.value.totalValueUsd) * newExchangeRate;
    field.onChange({ ...field.value, totalValueLocal: newLocalPrice, exchangeRate: newExchangeRate });
    recalculateExchangeRate();
  };

  const handleRemoveAction = () => {
    setIsRemoveModalOpen(true);
  };

  const handleAcceptRemoveAction = async () => {
    removeAction();
    setIsRemoveModalOpen(false);
  };

  const handleObservableServiceFields = () => {
    const { totalValueUsd, exchangeRate: serviceExchangeRate } = service || {};
    const exchangeRateToUse = serviceExchangeRate ? Number(serviceExchangeRate) : exchangeRate;
    const localPrice = Number(totalValueUsd) * exchangeRateToUse;

    if (totalValueUsd !== null && exchangeRateToUse) {
      if (localPriceInputMode === LOCAL_PRICE_INPUT_MODE.HIDDEN && !serviceExchangeRate) {
        return;
      }

      setValue(registerReference, {
        ...service,
        totalValueLocal: localPrice,
        exchangeRate: exchangeRateToUse.toString()
      });
    }
  };

  const getDisplayName = (value: typeof service): string =>
    value?.logisticServiceType?.name ??
    value?.configClass?.name ??
    (value?.configClassId != null ? configClasses?.find(c => c.id === value.configClassId)?.name : undefined) ??
    'Servicio logístico';

  const renderDropdownOptions = (
    field: ControllerRenderProps<
      ILogisticServiceEditionForm,
      `products.${number}.logisticServiceProductPrices.${number}`
    >
  ) =>
    unifiedDropdownOptions.map(option => {
      const isDisabled = !!field.value.logisticServiceProductId;
      const handleClick = () => {
        if (option.source === DROPDOWN_OPTION_SOURCE.LOGISTIC_SERVICE_TYPE) {
          field.onChange({
            ...field.value,
            logisticServiceTypeId: option.id,
            logisticServiceType: { id: option.id, name: option.name },
            configClassId: null,
            configClass: null
          });
        } else {
          field.onChange({
            ...field.value,
            configClassId: option.id,
            configClass: { id: option.id, name: option.name },
            logisticServiceTypeId: null,
            logisticServiceType: null
          });
        }
      };
      return (
        <button
          type="button"
          key={`${option.source}-${option.id}`}
          onClick={handleClick}
          className={`dropdownOption ${isDisabled ? 'disabled' : ''}`}
          disabled={isDisabled}
        >
          {option.name}
        </button>
      );
    });

  // NOTA: totalValueUsd cambia cuando totalValueUsd o exchangeRate cambian. Si se requiere tener en cuenta más parámetros, solo actualizar este useEffect
  useEffect(() => {
    handleObservableServiceFields();
  }, [service?.totalValueUsd, service?.exchangeRate, exchangeRate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getLocalPriceInputMode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Controller
      name={`${registerReference}`}
      control={control}
      render={({ field }) => {
        const isRowEditable = isProductPriceEditable(field.value);
        return (
          <section className="editableServiceRow">
            {isRemoveModalOpen && (
              <BordModalConfirm
                onCancel={() => setIsRemoveModalOpen(false)}
                onAccept={() => handleAcceptRemoveAction()}
                isLoading={false}
                isDisabled={false}
                title={
                  <p>
                    Estas por eliminar el ítem <strong>{getDisplayName(field.value)}</strong> de la herramienta{' '}
                    {productName} - serial: {productSerial} <br /> ¿seguro que deseas continuar?
                  </p>
                }
                textButtonAccept="Sí, eliminar item"
                textButtonCancel="No, cancelar"
              />
            )}
            <BordSelectDropdown
              key={`EditableServiceRow-${registerReference}-1`}
              selectOptions={renderDropdownOptions(field)}
              currentValueOrplaceholder={getDisplayName(field.value)}
              isFilled={getDisplayName(field.value) !== 'Servicio logístico'}
              label={'Descripción o concepto'}
              isDeactivated={!isRowEditable}
            />
            <NudosMaskedNumberInput
              key={`EditableServiceRow-${registerReference}-2`}
              isFilled={true}
              label={'Precio con IVA en USD'}
              placeholder="$0.00 USD"
              handleChange={text => field.onChange({ ...field.value, priceUsd: text, totalValueUsd: text })}
              numberOfDecimals={2}
              maskPrefix="$"
              maskSufix=" USD"
              currentValue={String(field.value.totalValueUsd || '')}
            />
            {showLocalPriceInput && (
              <NudosMaskedNumberInput
                key={`EditableServiceRow-${registerReference}-3`}
                isFilled={true}
                label={`Precio con IVA en ${getValues('currency')}`}
                placeholder={`$0.00 ${getValues('currency')}`}
                handleBlur={text => handleBlurLocalPrice(text, field)}
                numberOfDecimals={2}
                maskPrefix="$"
                maskSufix={` ${getValues('currency')}`}
                currentValue={String(field.value.totalValueLocal || '')}
                customClassName="localPriceInput"
                isDeactivated={localPriceInputMode === LOCAL_PRICE_INPUT_MODE.READONLY}
              />
            )}
            <div className="removeButtonSlot">
              {isRowEditable && (
                <NudosButton
                  customClassName="removeButton"
                  buttonText="Eliminar"
                  handleClick={() => handleRemoveAction()}
                  type="button"
                />
              )}
            </div>
          </section>
        );
      }}
    />
  );
};

export default EditableServiceRow;
